import type { Breakpoint } from '@ctw/config/zui';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import { DEFAULT_PAGE_SIZE } from '@ctw/shared/components/pagination/expand-list';
import { ExpandList } from '@ctw/shared/components/pagination/expand-list';
import { useContainerQuery } from '@ctw/shared/hooks/breakpoints';
import { type ClassName, tw, twx } from '@ctw/shared/utils/tailwind';
import { isEmpty } from 'lodash-es';
import { type ReactElement, type ReactNode, useEffect, useRef, useState } from 'react';
import { onlyText } from 'react-children-utilities';
import './resource-table.scss';
import type { FHIRModel } from '@ctw/shared/api/fhir/models/fhir-model';
// TODO: https://zeushealth.atlassian.net/browse/GAP-1163 should replace this with a ZusFhirResource or similar interface
import type { Resource } from 'fhir/r4';

export type PatientRiskGapsResourceTableProps<T> = {
  title: string;
  titleVisuallyHidden?: boolean;
  className?: string;
  columns: Array<PatientRiskGapsTableColumn<T>>;
  data: Array<T>;
  /** Defaults to 'No records found'. */
  emptyMessage?: string | ReactElement;
  isLoading?: boolean;
  onRowClick?: (args: { record: T; index: number }) => void;
  showTableHead?: boolean;
  /**
   * Renders a stacked list if inner table element of this component is at most this breakpoint size.
   * Defaults to 'sm'.
   */
  stackedBreakpoint?: Breakpoint;
  testId?: string;
  getRowClassName?: (record: T) => ClassName;
  hasSelectedRow?: boolean;
  selectedIndex?: number;
};

export type PatientRiskGapsTableColumn<T> = {
  title: ReactNode;
  titleVisuallyHidden?: boolean;
  className?: string;
  minimizedClassName?: string;
  headerClassName?: ClassName;
  widthPercent?: number;
  minWidth?: number | string;
  maxWidth?: number | string;
  width?: number | string;
  render: (record: T, shouldMinimize?: boolean) => ReactNode;
};

/**
 * The List View used in in the Patient Risk Gaps V2 UI.
 * Renders a list of Risk Gap MeasureReports.
 * When stacked, renders a sectioned list of Active and Closed Risk Gap sections.
 */
export function PatientRiskGapsResourceTable<T extends Resource, M extends FHIRModel<T>>({
  title,
  titleVisuallyHidden = false,
  className,
  columns,
  data: records,
  emptyMessage = 'No records found',
  isLoading = false,
  onRowClick,
  showTableHead = true,
  stackedBreakpoint = 'sm',
  testId = 'resource-table',
  getRowClassName,
  hasSelectedRow = false,
  selectedIndex,
}: PatientRiskGapsResourceTableProps<M>) {
  const containerRef = useRef<HTMLTableElement>(null);
  const { breakpoints } = useContainerQuery({ containerRef });
  const isStacked = breakpoints.isAtMost[stackedBreakpoint];
  const shouldMinimize = hasSelectedRow || isStacked;

  const [count, setCount] = useState(() => Math.min(DEFAULT_PAGE_SIZE, records.length));
  const hasData = !isLoading && records.length > 0;

  useEffect(() => {
    if (hasData) {
      setCount((c) => Math.min(Math.max(c, DEFAULT_PAGE_SIZE), records.length));
    } else {
      setCount(records.length);
    }
  }, [records.length, hasData]);

  const displayedRecords = records.slice(0, count);
  const getStatus = (record: M): string | undefined =>
    (record as unknown as { status?: string }).status;
  const activeRecords = displayedRecords.filter((record) => getStatus(record) !== 'closed-gap');
  const closedRecords = displayedRecords.filter((record) => getStatus(record) === 'closed-gap');
  const showHeaders =
    showTableHead && columns.some((col) => col.title && !isEmpty(`${col.title}`.trim()));

  return (
    <div
      data-testid={testId}
      className={twx(className, 'resource-table patient-risk-gap-resource-table')}
    >
      <div className={twx('relative overflow-hidden border-divider-main')}>
        <div
          className={twx('scroll-shadows overflow-x-auto', {
            'overflow-x-hidden': shouldMinimize,
          })}
        >
          <table ref={containerRef} className={tw`w-full table-auto border-collapse`}>
            {showHeaders && (
              <thead
                className={twx(
                  'border-background-subtle border-b border-solid bg-background-disabled',
                )}
              >
                <tr className={twx({ hidden: shouldMinimize })}>
                  {columns.map((column, index) => (
                    <th
                      key={`th-${title}-${onlyText(column.title)}-${index}`}
                      scope="col"
                      className={twx(
                        'group whitespace-nowrap px-4 py-3 text-left font-medium text-content-subtle text-xs uppercase tracking-wider',
                      )}
                    >
                      <span
                        className={twx(
                          { 'sr-only': column.titleVisuallyHidden ?? titleVisuallyHidden },
                          column.headerClassName,
                        )}
                      >
                        {column.title}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody className={twx({ 'h-[7rem]': records.length === 0 })}>
              {isLoading && (
                <tr
                  className={twx('flex flex-col items-center justify-center p-6', {
                    'relative p-3': shouldMinimize,
                  })}
                >
                  <td
                    className={tw`whitespace-pre-wrap px-3 py-4 text-sm`}
                    colSpan={columns.length}
                  >
                    <LoadingSpinner centered={true} message="Loading..." />
                  </td>
                </tr>
              )}
              {!isLoading && records.length === 0 && (
                <tr className={twx({ 'relative block p-3': shouldMinimize })}>
                  <td
                    className={tw`whitespace-pre-wrap px-3 py-4 text-sm`}
                    colSpan={columns.length}
                  >
                    <div
                      className={tw`pointer-events-none flex h-full min-h-full w-full items-center justify-center text-center text-content-subtle`}
                    >
                      {emptyMessage}
                    </div>
                  </td>
                </tr>
              )}
              <PatientRiskGapsResourceTableSectionHeader
                title={'Active'}
                itemCount={activeRecords.length}
                titleVisuallyHidden={titleVisuallyHidden}
                hidden={!(shouldMinimize || isLoading)}
              />
              {!isLoading &&
                activeRecords.map((record, rowIndex) => {
                  return (
                    <PatientRiskGapsResourceTableRow<T, M>
                      key={`row-active-${title}-${record.key}-${rowIndex}`}
                      variant="active"
                      record={record}
                      rowIndex={rowIndex}
                      title={title}
                      displayedRecords={displayedRecords}
                      selectedIndex={selectedIndex}
                      shouldMinimize={shouldMinimize}
                      onRowClick={
                        onRowClick as unknown as PatientRiskGapsResourceTableProps<T>['onRowClick']
                      }
                      getRowClassName={
                        getRowClassName as unknown as PatientRiskGapsResourceTableProps<T>['getRowClassName']
                      }
                      columns={
                        columns as unknown as PatientRiskGapsResourceTableProps<T>['columns']
                      }
                    />
                  );
                })}
              <PatientRiskGapsResourceTableSectionHeader
                title={'Closed'}
                itemCount={closedRecords.length}
                titleVisuallyHidden={titleVisuallyHidden}
                hidden={!(shouldMinimize || isLoading)}
              />
              {!isLoading &&
                closedRecords.map((record, rowIndex) => (
                  <PatientRiskGapsResourceTableRow<T, M>
                    key={`row-closed-${title}-${record.key}-${rowIndex}`}
                    variant="closed"
                    record={record}
                    rowIndex={rowIndex}
                    title={title}
                    displayedRecords={displayedRecords}
                    selectedIndex={selectedIndex}
                    shouldMinimize={shouldMinimize}
                    onRowClick={
                      onRowClick as unknown as PatientRiskGapsResourceTableProps<T>['onRowClick']
                    }
                    getRowClassName={
                      getRowClassName as unknown as PatientRiskGapsResourceTableProps<T>['getRowClassName']
                    }
                    columns={columns as unknown as PatientRiskGapsResourceTableProps<T>['columns']}
                  />
                ))}
            </tbody>
          </table>
        </div>
      </div>
      {!isLoading && records.length > 0 && (
        <ExpandList
          className={tw`mb-2 items-center justify-between gap-2.5 pr-4 pl-4`}
          total={records.length}
          count={count}
          pageSize={DEFAULT_PAGE_SIZE}
          changeCount={setCount}
        />
      )}
    </div>
  );
}

function PatientRiskGapsResourceTableSectionHeader({
  itemCount,
  title,
  hidden,
  titleVisuallyHidden,
}: {
  title: string;
  itemCount: number;
  hidden?: boolean;
  titleVisuallyHidden?: boolean;
}) {
  return itemCount > 0 ? (
    <tr
      className={twx('border-background-subtle border-b border-solid bg-background-disabled', {
        hidden,
      })}
    >
      <th
        colSpan={itemCount}
        scope="colgroup"
        className={twx(
          'group flex items-center justify-between whitespace-nowrap px-4 py-2 text-left font-medium text-content-subtle text-xs uppercase tracking-wider',
        )}
      >
        <span className={twx({ 'sr-only': titleVisuallyHidden })}>{title}</span>
        <span
          className={tw`rounded-full border border-background-icon border-solid bg-white px-1.5 py-0.5`}
        >
          {itemCount}
        </span>
      </th>
    </tr>
  ) : null;
}

interface PatientRiskGapsResourceTableRowProps<T extends Resource, M extends { key: string }> {
  /** Used for consistency in underlying key properties. */
  variant: 'active' | 'closed';
  /** title of the section */
  title: string;
  record: M;
  /** Defaults to zero.  */
  rowIndex: number;
  displayedRecords: Array<M>;
  selectedIndex?: number;
  shouldMinimize: boolean;
  /** Defaults to true. Set this to false when a parent components have a hover display. */
  hasBackgroundHover?: boolean;
  columns: PatientRiskGapsResourceTableProps<T>['columns'];
  getRowClassName?: PatientRiskGapsResourceTableProps<T>['getRowClassName'];
  onRowClick?: PatientRiskGapsResourceTableProps<T>['onRowClick'];
}

/**
 * Used for both closed and active row types. Also used in the patient-risk-gaps-overview-card.
 * Expected only for use within a nested {@link PatientRiskGapsResourceTable}.
 */
export function PatientRiskGapsResourceTableRow<T extends Resource, M extends { key: string }>({
  variant,
  title,
  record,
  rowIndex,
  displayedRecords,
  selectedIndex,
  shouldMinimize,
  columns,
  hasBackgroundHover = true,
  getRowClassName,
  onRowClick,
}: PatientRiskGapsResourceTableRowProps<T, M>) {
  const absoluteIndex = displayedRecords.findIndex((r) => r.key === record.key);
  const effectiveIndex = absoluteIndex >= 0 ? absoluteIndex : rowIndex;
  const getType = (record: M): string | undefined => (record as unknown as { type?: string }).type;
  const getSelectedRowBackgroundClass = (record: M): ClassName =>
    twx({
      'bg-info-background hover:bg-info-background': getType(record) === 'net-new',
      'bg-caution-background hover:bg-caution-background': getType(record) === 'suspect',
      'bg-success-background hover:bg-success-background': getType(record) === 'recapture',
      'bg-background-hover hover:bg-background-hover':
        getType(record) !== 'net-new' &&
        getType(record) !== 'suspect' &&
        getType(record) !== 'recapture',
    });
  return (
    <tr
      className={twx(
        'group relative table-row border-b border-b-background-subtle align-top',
        /* We can safely cast to unknown here since we're only ever using this within a PatientRiskGapsResourceTable */
        getRowClassName?.(record as unknown as T),
        {
          'hover:bg-background-subtle': hasBackgroundHover,
          'cursor-pointer hover:bg-background-hover': Boolean(onRowClick),
          'relative flex flex-row flex-wrap justify-between p-3': shouldMinimize,
        },
        effectiveIndex === selectedIndex && getSelectedRowBackgroundClass(record),
      )}
      /* We can safely cast to unknown here since we're only ever using this within a PatientRiskGapsResourceTable */
      onClick={() => onRowClick?.({ record: record as unknown as T, index: effectiveIndex })}
    >
      {columns.map((column, colIndex) => (
        <td
          key={`cell-${variant}-${title}-${onlyText(column.title)}-${record.key}-${colIndex}`}
          className={twx(
            'whitespace-pre-wrap break-words p-2 text-gray-900 text-sm',
            {
              'block w-full p-0 pr-7': shouldMinimize,
            },
            shouldMinimize ? column.minimizedClassName : column.className,
          )}
          style={{
            minWidth: column.minWidth,
            width: column.width,
            maxWidth: column.maxWidth,
          }}
        >
          {/* We can safely cast to unknown here since we're only ever using this within a PatientRiskGapsResourceTable */}
          {column.render(record as unknown as T, shouldMinimize)}
        </td>
      ))}
    </tr>
  );
}
